import React, { useEffect, useState } from 'react';
import { useAuthState } from '../hooks/useAuthState';
import { auth, db } from '../firebase';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserIcon,
  EnvelopeIcon as MailIcon,
  PhoneIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

// Animation variants for staggered animations
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.4 }
  }
};

// Loading skeleton component for better UX
const AccountSkeleton = () => (
  <div className="min-h-screen pt-16 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto animate-pulse">
    <div className="bg-white shadow rounded-lg p-6 mb-6">
      <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
      <div className="space-y-4">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="flex flex-col sm:flex-row sm:items-center">
            <div className="w-32 h-4 bg-gray-200 rounded"></div>
            <div className="w-48 h-4 bg-gray-200 rounded mt-2 sm:mt-0"></div>
          </div>
        ))}
      </div>
    </div>
    <div className="bg-white shadow rounded-lg p-6">
      <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
      <div className="space-y-4">
        {[1, 2].map(i => (
          <div key={i} className="h-32 bg-gray-200 rounded"></div>
        ))}
      </div>
    </div>
  </div>
);

/**
 * Account component displays the authenticated user's profile information
 * and a list of events they have registered for.
 * @param {{ loadingBarRef: React.MutableRefObject }} props - Props containing a ref to the top loading bar.
 */
function Account({ loadingBarRef }) {
  // Get the current authenticated user from Firebase with loading state.
  const [user, authLoading] = useAuthState(auth);
  // Hook for programmatic navigation.
  const navigate = useNavigate();
  // State to store the user's profile data from Firestore.
  const [userData, setUserData] = useState(null);
  // State to store the list of events the user is registered for.
  const [registeredEvents, setRegisteredEvents] = useState([]);
  // State to manage loading status.
  const [loading, setLoading] = useState(true);

  // useEffect hook to fetch all necessary data when the user is authenticated.
  useEffect(() => {
    // Don't redirect while authentication is still loading
    if (authLoading) {
      return;
    }
    
    // If there is no user after loading is complete, navigate to the home page.
    if (!user) {
      navigate('/');
      return;
    }

    /**
     * Fetches user profile data and their event registrations from Firestore.
     */
    const fetchData = async () => {
      try {
        // Start the top loading bar for visual feedback.
        if (loadingBarRef.current) {
          loadingBarRef.current.continuousStart();
        }

        // Fetch user's profile document from the 'users' collection.
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setUserData(userDoc.data());
        }

        // Fetch all events to get event details for registrations.
        const eventsSnapshot = await getDocs(collection(db, 'events'));
        const events = {};
        eventsSnapshot.forEach(doc => {
          events[doc.id] = { id: doc.id, ...doc.data() };
        });

        // Query for the user's registrations across all events.
        const registrations = [];
        for (const eventId of Object.keys(events)) {
          const registrationsRef = collection(db, `registrations/${eventId}/registrations`);
          const q = query(registrationsRef, where('userId', '==', user.uid));
          const querySnapshot = await getDocs(q);
          
          querySnapshot.forEach(doc => {
            registrations.push({
              registrationId: doc.id,
              eventId,
              eventDetails: events[eventId],
              ...doc.data()
            });
          });
        }

        // Update state with the fetched registrations.
        setRegisteredEvents(registrations);
      } catch (error) {
        // Log any errors during data fetching.
        console.error('Error fetching data:', error);
      } finally {
        // Stop loading state and complete the loading bar.
        setLoading(false);
        if (loadingBarRef.current) {
          loadingBarRef.current.complete();
        }
      }
    };

    fetchData();
  }, [user, authLoading, navigate, loadingBarRef]); // Dependencies for the effect.

  /**
   * Formats a Firestore timestamp or date object into a readable date string.
   * @param {object | string} timestamp - The timestamp to format.
   * @returns {string} A formatted date string (e.g., "MMMM dd, yyyy").
   */
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    // Handle both Firestore Timestamps and standard Date objects.
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return format(date, 'MMMM dd, yyyy');
  };

  /**
   * Determines the CSS classes for the status badge based on payment and scrutiny status.
   * @param {string} status - The payment status ('success' or other).
   * @param {string} scrutiny - The scrutiny status ('rejected', 'approved', or other).
   * @returns {string} Tailwind CSS classes for the badge.
   */
  const getStatusBadge = (status, scrutiny) => {
    const baseClasses = "px-3 py-1 rounded-full text-sm font-medium inline-flex items-center gap-1";
    // Green badge for successful payment.
    if (status === 'success') {
      return `${baseClasses} bg-green-100 text-green-800`;
    }
    // Red badge for rejected application.
    if (scrutiny === 'rejected') {
      return `${baseClasses} bg-red-100 text-red-800`;
    }
    // Yellow badge for pending status.
    return `${baseClasses} bg-yellow-100 text-yellow-800`;
  };

  // Show a skeleton loader while data is being fetched.
  if (loading) return <AccountSkeleton />;

  return (
    <motion.div 
      className="min-h-screen pt-16 pb-20 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Profile Details Section */}
      <motion.div 
        className="bg-white shadow rounded-lg p-6 mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Profile Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Map through profile items and display them */}
          {[
            { icon: UserIcon, label: 'Name', value: userData?.name || 'N/A' },
            { icon: MailIcon, label: 'Email', value: userData?.email || user.email },
            { icon: PhoneIcon, label: 'Phone', value: userData?.phone || 'N/A' },
            { icon: CalendarIcon, label: 'Joined', value: userData?.createdAt ? formatDate(userData.createdAt) : 'N/A' }
          ].map((item, index) => (
            <div key={index} className="flex items-start space-x-3">
              <item.icon className="w-5 h-5 text-indigo-600 mt-1" />
              <div>
                <p className="text-sm text-gray-500">{item.label}</p>
                <p className="font-medium text-gray-900">{item.value}</p>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Registered Events Section */}
      <motion.div 
        className="bg-white shadow rounded-lg p-6"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Registered Events</h2>
        
        <AnimatePresence>
          {/* Show a message if the user has no registered events */}
          {registeredEvents.length === 0 ? (
            <motion.div 
              className="text-center py-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <p className="text-gray-600 mb-4">You haven't registered for any events yet.</p>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Browse Events
              </button>
            </motion.div>
          ) : (
            // List of registered events
            <div className="space-y-6">
              {registeredEvents.map((registration) => {
                // Get the latest payment record for the registration.
                const latestPayment = registration.payments?.[registration.payments.length - 1];
                
                return (
                  <motion.div
                    key={registration.registrationId}
                    variants={itemVariants}
                    className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow duration-200"
                  >
                    {/* Event Header with Title and Status Badges */}
                    <div className="bg-gray-50 px-6 py-4 border-b">
                      <div className="flex flex-wrap items-center justify-between gap-4">
                        <h3 className="text-lg font-semibold text-indigo-600">
                          {registration.eventDetails?.title || 'Unknown Event'}
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {/* Payment Status Badge */}
                          <span className={getStatusBadge(registration.status, registration.scrutiny)}>
                            {registration.status === 'success' ? (
                              <>
                                <CheckCircleIcon className="w-4 h-4" />
                                Fees Paid
                              </>
                            ) : (
                              <>
                                <ClockIcon className="w-4 h-4" />
                                Payment Pending
                              </>
                            )}
                          </span>
                          {/* Scrutiny Status Badge */}
                          <span className={getStatusBadge('', registration.scrutiny)}>
                            {registration.scrutiny === 'approved' ? (
                              <>
                                <CheckCircleIcon className="w-4 h-4" />
                                Approved
                              </>
                            ) : registration.scrutiny === 'rejected' ? (
                              <>
                                <XCircleIcon className="w-4 h-4" />
                                Rejected
                              </>
                            ) : (
                              <>
                                <ClockIcon className="w-4 h-4" />
                                Under Scrutiny
                              </>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                    {/* Event Details Section */}
                    <div className="p-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                        {/* Registration ID */}
                        <div>
                          <p className="text-sm text-gray-500">Registration ID</p>
                          <p className="font-medium text-gray-900 break-words">{registration.registrationId}</p>
                        </div>
                        {/* Event Date */}
                        <div>
                          <p className="text-sm text-gray-500">Event Date</p>
                          <p className="font-medium text-gray-900">{formatDate(registration.eventDetails?.eventDate)}</p>
                        </div>
                        {/* Registration Date */}
                        <div>
                          <p className="text-sm text-gray-500">Registered On</p>
                          <p className="font-medium text-gray-900">{formatDate(registration.createdAt)}</p>
                        </div>
                        {/* Venue */}
                        <div>
                          <p className="text-sm text-gray-500">Venue</p>
                          <p className="font-medium text-gray-900">{registration.eventDetails?.venue}</p>
                        </div>
                        {/* Payment Status */}
                        <div>
                          <p className="text-sm text-gray-500">Payment Status</p>
                          <p className="font-medium text-gray-900">
                            {latestPayment ? `Paid ₹${latestPayment.amount}` : 'Pending'}
                          </p>
                        </div>
                        
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
}

export default Account;
